import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { api, formatApiErrorDetail } from "../lib/api";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { MAX_SIGNERS, isPJDocument, signerMissingFields } from "../lib/representatives";
import {
  ADVANCE_PERCENT_MAX,
  advanceExceedsFace,
  advanceExceedsFaceBannerCopy,
  advancePercentFromValue,
  advanceValueFromPercent,
  emptyReceivable,
  formatAdvancePercent,
  formatAllowedRange,
  getPolicyVerdict,
  moneyCents,
  isReceivableComplete,
  isReceivableTouched,
  mapReceivablePayload,
  policyRejectionReason,
  RECEIVABLE_FIELD_LABELS,
  receivableMissingFields,
  receivableEmptyFields,
  receivableInvalidFields,
  taxaOverrideError,
} from "../lib/operations";
import {
  CEDENTE_POLICY_REJECT_COPY,
  cedenteErrorInfo,
  clienteToPayer,
  clientesUrlForDeepLink,
  hasVisibleLiquidValue,
  pickCreatedCliente,
  resolveDeepLinkCliente,
  simulationDelayMs,
} from "../lib/cedenteOperations";
// MERGE FS-160 × Leva 1 (FS-159/161): a máscara de dinheiro do PCP é a CAIXA
// REGISTRADORA (`maskMoneyCentsBR` na digitação, `loadMoneyCentsBR` em TODA
// carga programática — o `prev` é inalcançável nela de propósito, FS-160 R2-7).
// A carga programática desta tela é a RESTAURAÇÃO DE RASCUNHO da FS-161.
import { loadMoneyCentsBR, finalizeMoneyBR, parseDecimalBR } from "../lib/numberFormat";
import { useAuth } from "../contexts/AuthContext";
import useUnsavedChangesWarning from "../hooks/useUnsavedChangesWarning";
import {
  DRAFT_DEBOUNCE_MS,
  clearDraft,
  draftSignature,
  draftUnavailableNotice,
  isDirtySince,
  loadDraft,
  resolveDraftCedente,
  resolveDraftProduct,
  saveDraft,
} from "../lib/operationDraft";
import {
  CAMPOS_DE_VALOR,
  CAMPOS_UI_ONLY,
  SIGNER_ERROR_RE,
  MODE_CFG,
  formatV2Detail,
} from "../components/newoperation/config";
import WizardHeader from "../components/newoperation/WizardHeader";
import RascunhoAvisos from "../components/newoperation/RascunhoAvisos";
import WizardStepper from "../components/newoperation/WizardStepper";
import StepProduto from "../components/newoperation/StepProduto";
import StepCedente from "../components/newoperation/StepCedente";
import StepRecebiveis from "../components/newoperation/StepRecebiveis";
import StepAssinantes from "../components/newoperation/StepAssinantes";
import StepRevisao from "../components/newoperation/StepRevisao";
import WizardActions from "../components/newoperation/WizardActions";

// `formatV2Detail` mudou para components/newoperation/config.js (R5); o export
// segue aqui para quem o importa deste arquivo.
export { formatV2Detail };

export default function NewOperation({ mode = "partner" }) {
  const cfg = MODE_CFG[mode] || MODE_CFG.partner;
  const isCedenteMode = mode === "cedente";
  const backPath = cfg.backPath();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [step, setStep] = useState(1);
  const [products, setProducts] = useState([]);
  const [productsLoading, setProductsLoading] = useState(true);
  // BO indisponível → fail-closed: todos os cards cinza + aviso (nunca sugerir habilitado).
  const [boUnavailable, setBoUnavailable] = useState(false);
  const [catalogState, setCatalogState] = useState(null);
  // Falha de INFRA ao carregar — estado distinto de "nenhum produto habilitado"
  // (negócio): não frasear como política. Guarda QUAIS superfícies falharam
  // (`products` | `me` | `clientes`) — "não carregou os clientes" é acionável,
  // "não carregou" não é.
  const [loadFailedParts, setLoadFailedParts] = useState([]);
  const loadError = loadFailedParts.length > 0;
  // Motivo do catálogo cinza vindo do servidor (`unavailable_reason`, aditivo):
  // `no_regent` (cadastro) ≠ `bo_down` (tente de novo) ≠ `internal`.
  const [catalogReason, setCatalogReason] = useState(null);
  const [cedentes, setCedentes] = useState([]);
  // Modo cedente: clientes do cedente = universo de SACADOS (sem digitação livre).
  const [clientes, setClientes] = useState([]);
  const [deepLinkUnresolved, setDeepLinkUnresolved] = useState(false);
  // Dialog inline de cadastro de cliente (Q5): guarda a LINHA que originou o
  // clique — o recém-criado é selecionado nela, não na primeira.
  const [clienteDialog, setClienteDialog] = useState({ open: false, rowKey: null, triggerId: null });
  // URL usada no load (com/sem `?status=active`, conforme o deep-link): a
  // recarga pós-cadastro reusa a MESMA — trocar o filtro no meio da sessão
  // faria clientes inativos aparecerem do nada.
  const clientesUrlRef = useRef(clientesUrlForDeepLink(searchParams.get("cliente_id")));
  const [submitting, setSubmitting] = useState(false);
  const [simulating, setSimulating] = useState(false);
  const [simulationResult, setSimulationResult] = useState(null);
  const [simulationError, setSimulationError] = useState(null);
  // Erro TERMINAL do modo cedente (fee_not_configured / no_policy_resolvable /
  // fee_invalid_value): nenhum ajuste de valor/prazo resolve — bloqueia já aqui,
  // em vez de deixar o usuário chegar à Revisão com um botão cinza.
  const [terminalError, setTerminalError] = useState(null);       // {message, scope}
  const terminalRef = useRef(null);
  // 429 no simulate: espaça a PRÓXIMA tentativa automática (o limite do backend
  // é por minuto — re-disparar a cada 600ms só cava mais fundo).
  const [rateLimited, setRateLimited] = useState(false);

  // ── FS-161 — anti-perda ────────────────────────────────────────────────────
  // `user.id` é a identidade do rascunho: sem ele NADA é persistido (o storage
  // é da ABA, e o rascunho de um usuário não pode aparecer para outro).
  const { user } = useAuth() || {};
  const userId = user?.id || null;
  // Operação CRIADA: desarma o aviso de saída e cala o autosave. Sem isto, a
  // navegação do sucesso ainda veria o formulário "sujo" e o debounce pendente
  // poderia RESSUSCITAR o rascunho recém-apagado.
  const [operationCreated, setOperationCreated] = useState(false);
  // Rascunho encontrado e AINDA NÃO decidido pelo usuário ({state, savedAt}).
  // A restauração nunca é silenciosa: repor valores de dinheiro sem pedir é a
  // variante de UX do erro de ×100.
  const [draftFound, setDraftFound] = useState(null);
  const draftCheckedRef = useRef(false);
  const draftBannerRef = useRef(null);
  // Confirmação do DESCARTE (destrutivo e sem desfazer): 2 passos, no molde do
  // AlertDialog de remoção de usuário (pages/Users.jsx).
  const [confirmDiscard, setConfirmDiscard] = useState(false);
  // O que a restauração DESCARTOU por não existir mais ({produto, cedente}).
  // Guardar o FATO, e não a frase, é o que faz o aviso sumir sozinho quando o
  // usuário resolve — ver `draftNotice`, derivado, logo abaixo do estado.
  const [draftDropped, setDraftDropped] = useState(null);
  // A última gravação FALHOU (quota/modo privado/storage bloqueado)? Sem isto a
  // tela prometeria "guardamos seu rascunho" para quem não tem rascunho nenhum.
  const [draftSaveFailed, setDraftSaveFailed] = useState(false);
  // Estado do formulário no momento de referência (ver `draftBaselineRef`) e
  // timer do debounce — em refs porque a SAÍDA precisa gravar AGORA (flush), de
  // dentro de um handler, sem esperar re-render.
  const draftStateRef = useRef(null);
  const draftTimerRef = useRef(null);
  // Baseline: assinatura do formulário DEPOIS do preenchimento programático
  // inicial (deep-link de cliente, cedente vindo do token). "Sujo" é divergir
  // dela — não "ter algo preenchido".
  const draftBaselineRef = useRef(null);
  // Destino pendente de confirmação ao sair com o formulário sujo (AlertDialog).
  const [exitTarget, setExitTarget] = useState(null);

  const [productId, setProductId] = useState("");
  const [cedenteId, setCedenteId] = useState(searchParams.get("cedente_id") || "");
  // Valor e prazo NAO sao mais digitados: derivam dos recebiveis (etapa 3).
  const [taxa, setTaxa] = useState("");
  const [descricao, setDescricao] = useState("");
  const [receivables, setReceivables] = useState([emptyReceivable()]);

  // Load inicial. `allSettled` em vez de `all`: uma superfície que cai não
  // apaga as que carregaram, e a mensagem pode dizer QUAL falhou.
  const loadInitial = async () => {
    setProductsLoading(true);
    const preselectedCliente = searchParams.get("cliente_id");
    const preselectedCedente = searchParams.get("cedente_id");
    const pedidos = isCedenteMode
      ? [
          ["products", cfg.endpoints.products],
          ["me", cfg.endpoints.me],
          ["clientes", clientesUrlRef.current],
        ]
      : [
          ["products", cfg.endpoints.products],
          ["me", preselectedCedente ? "/cedentes" : "/cedentes?status=active"],
        ];
    const resultados = await Promise.allSettled(pedidos.map(([, url]) => api.get(url)));
    const falhas = [];
    const ok = {};
    resultados.forEach((r, i) => {
      const chave = pedidos[i][0];
      if (r.status === "fulfilled") ok[chave] = r.value;
      else {
        falhas.push(chave);
        console.error(`[NewOperation] Falha ao carregar ${chave}:`, r.reason);
      }
    });

    if (ok.products) {
      const data = ok.products.data;
      const list = Array.isArray(data) ? data : (data?.products || []);
      // Enabled first, then disabled
      setProducts([...list].sort((a, b) => (b.enabled ? 1 : 0) - (a.enabled ? 1 : 0)));
      setBoUnavailable(!!data?.bo_unavailable);
      // Campo aditivo do catálogo ORIGINADOR. Cedente conserva o contrato próprio.
      setCatalogState(isCedenteMode ? null : (data?.catalog_state || null));
      setCatalogReason(data?.unavailable_reason || null);
    }
    if (ok.me) {
      if (isCedenteMode) {
        // O cedente É o usuário logado (objeto único), não uma lista.
        const meCedente = ok.me.data || null;
        setCedentes(meCedente ? [meCedente] : []);
        setCedenteId(meCedente?.id || "");
      } else {
        setCedentes(ok.me.data);
      }
    }
    if (ok.clientes) {
      const clientesList = Array.isArray(ok.clientes.data) ? ok.clientes.data : [];
      setClientes(clientesList);
      const { cliente, unresolved } = resolveDeepLinkCliente(clientesList, preselectedCliente);
      setDeepLinkUnresolved(unresolved);
      if (cliente) {
        // Deep-link resolvido: vira o sacado da PRIMEIRA linha (as demais
        // seguem em branco — a escolha é por recebível).
        setReceivables((prev) =>
          prev.map((r, i) => (i === 0 ? { ...r, payer_cliente_id: cliente.id, ...clienteToPayer(cliente) } : r))
        );
      }
    }
    setLoadFailedParts(falhas);
    setProductsLoading(false);
  };

  useEffect(() => {
    loadInitial();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const product = useMemo(() => products.find((p) => p.id === productId), [products, productId]);
  const enabledCount = useMemo(() => products.filter((p) => p.enabled).length, [products]);
  const cedente = useMemo(() => cedentes.find((c) => c.id === cedenteId), [cedentes, cedenteId]);
  // `taxa` guarda a string MASCARADA em pt-BR ("3,5") — `parseFloat` leria 3 e
  // o `taxa_mensal` do payload iria ERRADO. Vazio → 0, idêntico ao antigo
  // `parseFloat(taxa || 0)`.
  const taxaNum = parseDecimalBR(taxa) ?? 0;
  // Taxa PREENCHIDA que não parseia bloqueia a ação (regra pura e testada em
  // lib/operations.js — a mesma que o `stockFeesError` do Stock usa).
  const taxaErro = !isCedenteMode && cfg.showRates ? taxaOverrideError(taxa) : null;

  // PJ = CNPJ (14 dígitos); PF = CPF (11). Define o fluxo de etapas (Assinantes
  // só p/ PJ) e o envio de signer_ids no create.
  const cedenteIsPJ = useMemo(() => isPJDocument(cedente?.cnpj), [cedente]);
  const stepFlow = cedenteIsPJ ? cfg.flowPJ : cfg.flowPF;
  const stepKey = stepFlow[step - 1];
  const totalSteps = stepFlow.length;

  // ── Assinantes (etapa PJ, item 22) ─────────────────────────────────────
  // signers=null → ainda não carregado p/ o cedente atual (recarrega ao entrar
  // na etapa); selectedSignerIds é ORDENADO — a ordem define a posição de
  // assinatura no contrato (signer 1 + QUALIFICACAO-SOCIO-N).
  const [signers, setSigners] = useState(null);
  const [signersLoading, setSignersLoading] = useState(false);
  const [signersError, setSignersError] = useState(null);
  const [selectedSignerIds, setSelectedSignerIds] = useState([]);
  const [repDialog, setRepDialog] = useState({ open: false, rep: null });
  // Descarta resposta fora de ordem do loadSigners (mesmo padrão do
  // simSeqRef): entrada na etapa, retry e onSaved do dialog concorrem — uma
  // lista velha nunca sobrescreve a fresca (ex.: representante recém-criado
  // "sumindo" quando a resposta antiga chega por último).
  const signersSeqRef = useRef(0);

  // Base dos endpoints de representante (dialog e listagem): no modo cedente o
  // id do cedente vem do token, então o path não o carrega.
  const repBasePath = cfg.endpoints.repBase(cedenteId);

  /**
   * Erro de API → mensagem exibível, por modo.
   * - parceiro: `formatV2Detail` (texto da V2, inclui faixa de taxa).
   * - cedente: tradução por CÓDIGO — nunca o texto cru (cita taxa/policy),
   *   nunca `JSON.stringify` na tela.
   */
  const describeApiError = (err, fallback) => {
    const detail = err?.response?.data?.detail;
    if (isCedenteMode) return cedenteErrorInfo(detail, err?.response?.status ?? 0);
    return { code: null, message: formatV2Detail(detail, fallback), terminal: false, transient: false };
  };

  // Qualquer alteracao de dado que afeta a simulacao (recebiveis, taxa,
  // cedente) invalida a simulacao vigente — nada de resultado stale na tela;
  // o debounce abaixo recalcula quando os dados voltam a ficar completos.
  const clearSimulation = () => {
    setSimulationResult(null);
    setSimulationError(null);
  };
  const handleTaxaChange = (value) => {
    setTaxa(value);
    clearSimulation();
  };
  const handleProductChange = (value) => {
    setProductId(value);
    // Round 4 / B1: no modo PARCEIRO isto é EXATAMENTE o `setProductId` de
    // antes — nem limpeza de terminal (que não existe lá), nem
    // `clearSimulation` (que o fluxo do parceiro nunca fez ao trocar de
    // produto). A tela do parceiro fica byte-idêntica.
    if (!isCedenteMode) return;
    // Só no modo cedente: trocar de produto é a ÚNICA ação do usuário que pode
    // resolver um erro terminal (a configuração é por produto) — o bloqueio sai
    // e a simulação decide de novo.
    setTerminalError(null);
    clearSimulation();
  };
  const handleCedenteChange = (value) => {
    setCedenteId(value);
    clearSimulation();
    // Assinantes são POR cedente: troca de cedente zera lista e seleção (a
    // etapa recarrega e re-pré-seleciona os padrões ao ser aberta) e invalida
    // resposta em voo do cedente anterior. signersLoading volta a false AQUI
    // (o finally da chamada superada não limpa — é o load novo que manda).
    signersSeqRef.current += 1;
    setSigners(null);
    setSignersLoading(false);
    setSignersError(null);
    setSelectedSignerIds([]);
  };

  // Carrega os representantes ATIVOS do cedente; preselect=true (primeira
  // abertura p/ o cedente) pré-seleciona os "assinante padrão" do cadastro.
  const loadSigners = async ({ preselect = false } = {}) => {
    if (!cedenteId) return;
    const seq = ++signersSeqRef.current;
    setSignersLoading(true);
    setSignersError(null);
    try {
      const { data } = await api.get(`${repBasePath}/representatives`, {
        params: { status: "active", limit: 200 },
      });
      if (seq !== signersSeqRef.current) return; // resposta superada: descarta
      const items = data.items || [];
      setSigners(items);
      if (preselect) {
        setSelectedSignerIds(
          items.filter((r) => r.is_default_signer).slice(0, MAX_SIGNERS).map((r) => r.id)
        );
      } else {
        // Recarga (ex.: cadastro completado no atalho): poda seleção de quem
        // deixou de estar ativo — nunca enviar id inválido por estado stale.
        setSelectedSignerIds((prev) => prev.filter((id) => items.some((r) => r.id === id)));
      }
    } catch (err) {
      if (seq !== signersSeqRef.current) return; // resposta superada: descarta
      // Fail-closed: sem lista confiável não há como validar a seleção — a
      // etapa mostra o erro com retry e o avanço fica bloqueado.
      setSigners([]);
      setSelectedSignerIds([]);
      setSignersError(
        isCedenteMode
          ? describeApiError(err).message
          : formatApiErrorDetail(err?.response?.data?.detail)
      );
    } finally {
      if (seq === signersSeqRef.current) setSignersLoading(false);
    }
  };

  useEffect(() => {
    if (stepKey === "assinantes" && signers === null && !signersLoading) {
      // `preselect` SÓ quando não há seleção: numa entrada normal a seleção
      // está vazia e o comportamento é o de sempre (os assinantes padrão do
      // cadastro vêm marcados). Com rascunho restaurado (FS-161) JÁ há seleção
      // — os padrões não podem sobrescrever a escolha guardada; o ramo sem
      // preselect só poda quem deixou de estar ativo, que é o que se quer de um
      // estado que dormiu.
      loadSigners({ preselect: selectedSignerIds.length === 0 });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stepKey, signers]);

  const selectedSigners = useMemo(
    () => selectedSignerIds.map((id) => (signers || []).find((r) => r.id === id)).filter(Boolean),
    [selectedSignerIds, signers]
  );
  const incompleteSelected = useMemo(
    () => selectedSigners.filter((r) => signerMissingFields(r).length > 0),
    [selectedSigners]
  );

  const toggleSigner = (id) => {
    if (selectedSignerIds.includes(id)) {
      setSelectedSignerIds(selectedSignerIds.filter((x) => x !== id));
    } else if (selectedSignerIds.length >= MAX_SIGNERS) {
      toast.error(`Máximo de ${MAX_SIGNERS} assinantes por contrato — remova um para adicionar outro.`);
    } else {
      setSelectedSignerIds([...selectedSignerIds, id]);
    }
  };

  // Receivable helpers
  const addReceivable = () => {
    setReceivables((prev) => [...prev, emptyReceivable()]);
    clearSimulation();
  };
  const removeReceivable = (key) => {
    setReceivables((prev) => prev.filter((r) => r._key !== key));
    clearSimulation();
  };
  /**
   * Escrita ATÔMICA de N campos da linha (FS-162).
   *
   * O percentual é bidirecional, e é aqui que a ausência de LOOP fica por
   * construção: editar o % escreve `requested_advance_value` **e** `_pctInput`
   * no MESMO `setReceivables`. Com duas chamadas separadas haveria um render
   * intermediário em que os dois discordam — e qualquer tentativa de
   * re-sincronizar por `useEffect` seria o antipadrão que o plano veta.
   */
  const updateReceivableFields = (key, fields) => {
    const antes = receivables.find((r) => r._key === key);
    // A simulação só morre quando muda DADO. Campo de UI (`_pctInput`) nunca
    // conta; campo de dinheiro conta só se o NÚMERO mudou (apagar o zero final
    // de "R$ 9.500,00" muda a string, não o valor — invalidar aí apagaria a
    // simulação SEM trazê-la de volta, porque o `simSignature` segue igual).
    const mudaDado =
      !antes ||
      Object.entries(fields).some(([campo, valor]) => {
        if (CAMPOS_UI_ONLY.includes(campo)) return false;
        // `Object.is` para que NaN (inválido) conte como igual a NaN.
        if (CAMPOS_DE_VALOR.includes(campo))
          return !Object.is(parseDecimalBR(antes[campo]), parseDecimalBR(valor));
        return true; // demais campos: comportamento histórico
      });
    setReceivables((prev) => prev.map((r) => (r._key === key ? { ...r, ...fields } : r)));
    if (mudaDado) clearSimulation();
  };
  const updateReceivable = (key, field, value) => updateReceivableFields(key, { [field]: value });

  // ── FS-162: os dois campos de dinheiro e o percentual entre eles ───────────
  //
  // O único número da linha continua sendo `requested_advance_value`. O % não
  // existe no estado: ou é o texto em foco (`_pctInput`), ou é derivado do
  // valor. Isso é o que torna o loop impossível — não há dois números para
  // sincronizar.

  /**
   * Q1 — "default 100%". Enquanto NINGUÉM decidiu o valor a antecipar, ele
   * ACOMPANHA o valor de face: quem antecipa a nota inteira (o caso comum)
   * digita UM número. Depois do primeiro toque o valor é a verdade e fica onde
   * está — quem já escreveu R$ 5.000,00 não pode ver esse número trocado
   * sozinho ao corrigir um dígito do valor de face.
   */
  const updateNetFaceValue = (key, value) => {
    const r = receivables.find((x) => x._key === key);
    const acompanha = !!r && !r._advanceTouched;
    updateReceivableFields(key, {
      net_face_value: value,
      // O % é sempre relativo ao valor de face: mudou a BASE, o texto que
      // estava no campo % deixou de descrever a linha. Volta a DERIVADO — sem
      // isto, corrigir o valor de face deixaria "70" escrito num campo que
      // agora vale 35%, e o próximo blur/tecla recalcularia o dinheiro a partir
      // de um percentual velho.
      _pctInput: null,
      ...(acompanha
        ? { requested_advance_value: advanceValueFromPercent(value, ADVANCE_PERCENT_MAX) ?? "" }
        : {}),
    });
  };

  /** Editar o VALOR devolve o % ao estado derivado (o valor é a fonte). */
  const updateAdvanceValue = (key, value) =>
    updateReceivableFields(key, {
      requested_advance_value: value,
      _pctInput: null,
      _advanceTouched: true,
    });

  /**
   * Editar o PERCENTUAL recalcula o valor — a única direção em que o % manda,
   * e só a partir do texto DIGITADO (nunca do % exibido: re-derivar do
   * arredondado não é idempotente e encolheria o valor a cada ciclo).
   */
  const updateAdvancePercent = (key, texto) => {
    const r = receivables.find((x) => x._key === key);
    if (!r) return;
    const vazio = String(texto ?? "").trim() === "";
    const pct = parseDecimalBR(texto);

    // ── Teto de 100: rejeitar SUBIDA, liberar DESCIDA (UX-1) ────────────────
    // Acima do teto o texto é REJEITADO, não clampado (R1): reescrever "101"
    // como "100" trocaria o número que a pessoa acabou de digitar.
    //
    // Mas rejeitar por valor ABSOLUTO travava o campo justamente no estado em
    // que a mensagem manda editá-lo: com o valor acima do valor de face o campo
    // exibe "150,00", e todo estado intermediário do backspace ("150,0", "150")
    // também parseia acima de 100 — o campo ficava INERTE, e a copy "reduza o
    // valor ou o percentual" apontava para um controle que não respondia.
    //
    // O teto efetivo passa a ser `max(atual, 100)`: nunca se SOBE acima de 100
    // (R1 intacto — de um campo vazio ou em 10, "101" segue recusado), mas
    // qualquer edição que DIMINUA o número é aceita.
    if (Number.isFinite(pct) && pct > ADVANCE_PERCENT_MAX) {
      const atual = parseDecimalBR(percentualTexto(r));
      const tetoEfetivo = Number.isFinite(atual)
        ? Math.max(atual, ADVANCE_PERCENT_MAX)
        : ADVANCE_PERCENT_MAX;
      if (pct > tetoEfetivo) return;
      // Descida ainda fora da faixa: só o TEXTO anda. Recalcular aqui zeraria o
      // valor (nenhum % acima de 100 gera valor) e apagaria, no meio da
      // correção, o dinheiro que se está corrigindo.
      updateReceivableFields(key, { _pctInput: texto, _advanceTouched: true });
      return;
    }

    // Texto ainda incompleto ("70,"): só o texto anda. Zerar o valor a cada
    // tecla intermediária piscaria "Falta preencher" no meio da digitação.
    if (!vazio && !Number.isFinite(pct)) {
      updateReceivableFields(key, { _pctInput: texto, _advanceTouched: true });
      return;
    }
    updateReceivableFields(key, {
      // Q3: 0% e campo vazio deixam o valor VAZIO ("Falta preencher"), nunca
      // R$ 0,00 — o contrato da V2 é `gt=0` e a máscara não escreve zero.
      requested_advance_value: (vazio ? null : advanceValueFromPercent(r.net_face_value, pct)) ?? "",
      _pctInput: texto,
      // UX-6: APAGAR o campo não é decidir um valor — é desfazer. A linha volta
      // a "ninguém decidiu ainda" e o default 100% (Q1) fica alcançável de
      // novo; sem isto, quem clicasse no % por curiosidade e apagasse perdia o
      // "acompanha o valor de face" para sempre naquela linha, sem aviso e sem
      // caminho de volta. Linha JÁ tocada permanece tocada.
      ...(vazio ? {} : { _advanceTouched: true }),
    });
  };

  /**
   * Sair do campo devolve o % ao DERIVADO — EXCETO quando o texto digitado
   * ainda descreve exatamente o valor que está no campo de reais (UX-3).
   *
   * Sem a exceção, qualquer valor de face com centavos "quebrados" (o caso
   * normal de uma NF) fazia o número recém-digitado virar outro na frente do
   * usuário: digita 70 em R$ 3.333,33 e o campo mostra 69,99 ao sair. O texto
   * PEGAJOSO não toca dinheiro nenhum — é o mesmo `requested_advance_value`
   * com ou sem ele —, e o aviso de aproximação continua dizendo quem manda no
   * envio. Assim que o texto deixa de reproduzir o valor (o usuário editou os
   * reais, ou a base mudou), ele cai e o derivado volta.
   */
  const blurAdvancePercent = (key) => {
    const r = receivables.find((x) => x._key === key);
    if (!r) return;
    const digitado = r._pctInput;
    const reproduzOValor =
      digitado !== null &&
      digitado !== undefined &&
      // Separador PENDURADO ("70,") ainda parseia como 70 e portanto "descreve"
      // o valor — mas é texto no MEIO de uma digitação. Deixá-lo depois do blur
      // é a versão-percentual de um campo de dinheiro parado em "R$ 10.000,";
      // é para isso que o `finalizeMoneyBR` existe no blur dos vizinhos. Cai, e
      // o derivado — já canônico — assume.
      !/[.,]$/.test(digitado) &&
      advanceValueFromPercent(r.net_face_value, parseDecimalBR(digitado)) ===
        r.requested_advance_value;
    if (!reproduzOValor) updateReceivableFields(key, { _pctInput: null });
  };

  // Blur do campo de dinheiro: completa as casas ("R$ 10.000" -> "R$ 10.000,00").
  // Não marca `_advanceTouched`: aqui só a FORMA muda, e um blur não é uma
  // decisão sobre quanto antecipar.
  const finalizeMoneyField = (key, field, value) => {
    const done = finalizeMoneyBR(value);
    if (done === value) return;
    if (field === "net_face_value") updateNetFaceValue(key, done);
    else updateReceivableFields(key, { [field]: done });
  };

  // % DERIVADO do valor (e a exatidão, que decide o aviso de aproximação — Q2).
  const percentualDerivado = (r) =>
    advancePercentFromValue(r.net_face_value, r.requested_advance_value);
  // Sem valor de face legível não há percentual possível: campo DESABILITADO,
  // nunca "NaN%".
  const percentualIndisponivel = (r) => moneyCents(r.net_face_value) === null;
  const percentualTexto = (r) => {
    if (percentualIndisponivel(r)) return "";
    if (r._pctInput !== null && r._pctInput !== undefined) return r._pctInput;
    return formatAdvancePercent(percentualDerivado(r)?.percent);
  };
  /**
   * Aviso quando o % de 2 casas NÃO representa o valor exatamente.
   *
   * A condição é sobre o FATO (a divisão não fecha), não sobre o foco. Antes
   * ela exigia `_pctInput === null`, o que tornava o aviso estruturalmente
   * inalcançável para leitor de tela (UI-2): ele só existia quando o campo
   * NÃO estava sendo digitado, e `aria-describedby` só é lido ao GANHAR foco —
   * o aviso nascia sempre com o foco em outro lugar. Agora ele vale também
   * enquanto se digita e enquanto o texto está pegajoso (UX-3), que é
   * exatamente quando o usuário precisa saber que 70 e 69,99 são o mesmo
   * pedido.
   */
  const percentualAproximado = (r) =>
    !percentualIndisponivel(r) && percentualDerivado(r)?.exact === false;
  // Sacado por LINHA no modo cedente: a seleção preenche payer_name/document
  // (o payload enviado é EXATAMENTE o mesmo do modo parceiro).
  // `payer_cliente_id` é estado só de UI — `mapReceivablePayload` não o envia.
  const selectPayerCliente = (key, clienteId) => {
    const cli = clientes.find((c) => c.id === clienteId) || null;
    setReceivables((prev) =>
      prev.map((r) => (r._key === key ? { ...r, payer_cliente_id: clienteId, ...clienteToPayer(cli) } : r))
    );
    setDeepLinkUnresolved(false);
    clearSimulation();
  };

  // Cadastro INLINE de cliente (Q5): o POST já respondeu com a linha criada.
  // Recarrega a lista com o MESMO filtro do load e seleciona o recém-criado na
  // linha que originou o clique. Se a recarga falhar, o cliente EXISTE do mesmo
  // jeito — entra na lista local (veio do servidor) em vez de sumir da tela.
  const handleClienteCreated = async (created) => {
    const rowKey = clienteDialog.rowKey || receivables[0]?._key;
    let list;
    try {
      const { data } = await api.get(clientesUrlRef.current);
      list = Array.isArray(data) ? data : [];
    } catch (e) {
      console.error("[NewOperation] Falha ao recarregar clientes apos cadastro:", e);
      list = created?.id && !clientes.some((c) => c.id === created.id)
        ? [...clientes, created]
        : clientes;
    }
    setClientes(list);
    const id = pickCreatedCliente(list, created);
    if (!rowKey || !id) return; // nunca selecionar o cliente ERRADO
    const cli = list.find((c) => c.id === id) || null;
    setReceivables((prev) =>
      prev.map((r) => (r._key === rowKey ? { ...r, payer_cliente_id: id, ...clienteToPayer(cli) } : r))
    );
    setDeepLinkUnresolved(false);
    clearSimulation();
  };

  const openClienteDialog = (rowKey, triggerId) =>
    setClienteDialog({ open: true, rowKey, triggerId });

  // Item E: os predicados saíram para lib/operations.js porque a tela agora
  // precisa saber QUAIS campos faltam (e não só que falta algum) — semântica
  // preservada byte a byte, o gate de avanço não mudou.
  const isValidReceivable = isReceivableComplete;
  const isFilledReceivable = isReceivableTouched;

  // Campos faltantes que a linha deve marcar em VERMELHO. Só depois que a linha
  // tem algum dado — linha pristine não é erro (a11y: nada de `aria-invalid` no
  // estado inicial). Generaliza o que antes só o Select de sacado fazia.
  const camposFaltando = (r) =>
    isFilledReceivable(r) ? receivableMissingFields(r) : [];
  const faltando = (r, campo) => camposFaltando(r).includes(campo);

  // Rótulos dos campos faltantes, na ordem do formulário. No modo cedente,
  // sacado e documento vêm do MESMO Select ("Clientes (sacados)"): listar os
  // dois diria que faltam dois campos onde a tela mostra um.
  const rotular = (chaves) =>
    chaves
      .filter((c) => !(cfg.payerFromClientes && c === "payer_name"))
      .map((c) =>
        cfg.payerFromClientes && c === "payer_document"
          ? "cliente (sacado)"
          : RECEIVABLE_FIELD_LABELS[c]
      );
  const rotulosFaltando = (r) => rotular(camposFaltando(r));
  // FS-160 (UX-5): campo PREENCHIDO com valor INVÁLIDO não é campo vazio. A
  // máscara deixa o texto cru VISÍVEL de propósito (doutrina 2), então "Falta
  // preencher: valor" com o valor na tela era literalmente falso — e não dizia
  // o que corrigir. As duas listas são disjuntas e cobrem `camposFaltando`.
  const rotulosVazios = (r) => (isFilledReceivable(r) ? rotular(receivableEmptyFields(r)) : []);
  const rotulosInvalidos = (r) => (isFilledReceivable(r) ? rotular(receivableInvalidFields(r)) : []);

  const payerInvalido = (r) => faltando(r, "payer_document");

  const validReceivables = useMemo(
    () => receivables.filter(isValidReceivable),
    [receivables]
  );

  // Linha PARCIAL (algo digitado mas incompleta): bloqueia o avanco — o payload
  // envia so os validos e uma linha parcial seria descartada em silencio.
  const hasPartialReceivable = useMemo(
    () => receivables.some((r) => isFilledReceivable(r) && !isValidReceivable(r)),
    [receivables]
  );

  /**
   * UX-4 — o banner do passo derivado do FATO, não de um motivo presumido.
   *
   * Este banner é o único elemento deste conjunto com `role="alert"`: é a frase
   * que um leitor de tela realmente anuncia. Ele dizia "Preencha todos os
   * campos obrigatórios" também no estado da Q4, em que nada falta — mandando
   * procurar um campo vazio inexistente. Mesma lição do C5 da Leva 1: a copy
   * sai do estado, não de uma suposição sobre ele.
   *
   * Toda linha reprovada reprova SÓ pelo teto ⇒ a frase é a do teto; havendo
   * qualquer linha de fato incompleta, a copy histórica volta INTACTA (e ganha
   * a frase do teto ao lado quando as duas situações coexistem).
   */
  const reprovadas = useMemo(
    () => receivables.filter((r) => isFilledReceivable(r) && !isValidReceivable(r)),
    [receivables]
  );
  const temLinhaIncompleta = reprovadas.some((r) => receivableMissingFields(r).length > 0);
  const temLinhaAcimaDoTeto = reprovadas.some(advanceExceedsFace);
  const avisoRecebivelParcial = [
    temLinhaIncompleta ? cfg.copy.recebivelParcial : null,
    temLinhaAcimaDoTeto ? advanceExceedsFaceBannerCopy(cfg.copy.valorNotaLabelCurto) : null,
  ]
    .filter(Boolean)
    .join(" ");

  // Soma SOBRE os recebiveis VALIDOS — exatamente os enviados no payload e
  // listados na revisao (somar linhas parciais divergiria do enviado).
  // Round 2 casas: soma de floats.
  const totalReceivables = useMemo(
    () => Math.round(
      validReceivables.reduce((sum, r) => sum + (parseDecimalBR(r.requested_advance_value) || 0), 0) * 100
    ) / 100,
    [validReceivables]
  );

  // Prazo derivado dos recebiveis: dias ate o MAIOR vencimento (1..365 —
  // faixa aceita pelo payload do backend; campo legado de exibicao local).
  const prazoDias = useMemo(() => {
    if (validReceivables.length === 0) return 0;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const days = validReceivables.map((r) =>
      Math.round((new Date(`${r.due_date}T00:00:00`) - today) / 86400000)
    );
    return Math.min(365, Math.max(1, ...days));
  }, [validReceivables]);

  // Simulation — chamada pelo debounce automatico E pelo botao manual (fallback).
  // `simSeqRef` descarta respostas fora de ordem (resposta antiga nunca
  // sobrescreve a mais recente); `simAbortRef` cancela a request EM VOO quando
  // uma nova dispara, quando os dados mudam ou no unmount (sem setState órfão).
  const simSeqRef = useRef(0);
  const simAbortRef = useRef(null);
  const runSimulation = async () => {
    if (validReceivables.length === 0) return;
    if (taxaErro) {
      setSimulationResult(null);
      setSimulationError(taxaErro);
      return;
    }
    const seq = ++simSeqRef.current;
    simAbortRef.current?.abort();
    const controller = new AbortController();
    simAbortRef.current = controller;
    // Timeout ESCOPADO da simulacao (nao mexe no timeout global da instancia
    // axios — afetaria todos os endpoints): sem ele, V2 travada deixaria
    // simulating=true para sempre, e com o gate do submit exigindo simulacao
    // valida a UI ficaria sem saida (botao manual disabled, retry so aparece
    // com !simulating). `timedOut` distingue abort por timeout (erro amigavel
    // + retry) de abort por supersede/troca de step/unmount (silencio).
    let timedOut = false;
    const timeoutTimer = setTimeout(() => {
      timedOut = true;
      controller.abort();
    }, 20000);
    setSimulating(true);
    setSimulationError(null);
    try {
      // Mesmo mapper do create (inclui backing_type): o proxy /v2/simulate
      // reconstrói o corpo só com os campos tipados de SimulateReceivable —
      // o campo extra é descartado lá e não afeta a simulação.
      const body = { receivables: validReceivables.map(mapReceivablePayload) };
      if (!isCedenteMode) {
        // No modo cedente o `assignor_document` é derivado do TOKEN no servidor
        // (o schema nem aceita o campo) e não existe ramo de `fees`.
        body.assignor_document = cedente?.cnpj?.replace(/[.\-/]/g, "") || undefined;
        if (cfg.showRates && taxaNum > 0) {
          body.fees = { monthly_rate_pct: taxaNum };
        }
      }
      const { data } = await api.post(cfg.endpoints.simulate, body, { signal: controller.signal });
      if (seq !== simSeqRef.current) return;
      setSimulationResult(data);
      setTerminalError(null);
      setRateLimited(false);
    } catch (e) {
      if (seq !== simSeqRef.current) return;
      if (controller.signal.aborted) {
        if (timedOut) {
          // Spinner cai (finally), retry aparece, submit segue bloqueado
          // ate uma simulacao valida.
          setSimulationResult(null);
          setSimulationError("Simulacao demorou demais — tente novamente.");
        }
        return; // abort de supersede/step/unmount: nao e erro de negocio
      }
      setSimulationResult(null);
      const info = describeApiError(e, "Falha na simulacao");
      setRateLimited(!!info.rateLimited);
      if (info.terminal) {
        // Terminal: para o fluxo AQUI (o card de bloqueio assume a tela).
        // `scope` decide o apoio: preço ⇒ "tente outro produto"; conta ⇒ nada
        // sobre produto/recebíveis (não há o que o usuário mexa aqui).
        setTerminalError({ message: info.message, scope: info.scope });
        setSimulationError(null);
        return;
      }
      const msg = info.message;
      setSimulationError(
        !isCedenteMode && typeof msg === "string" && (msg.includes("não configurada") || msg.includes("indisponível"))
          ? "Simulacao V2 indisponivel no momento. Tente novamente em instantes."
          : msg
      );
    } finally {
      clearTimeout(timeoutTimer);
      if (seq === simSeqRef.current) setSimulating(false);
    }
  };

  // Unmount: invalida respostas em voo (simulação e assinantes) e aborta a
  // request de simulação pendente.
  useEffect(() => () => {
    simSeqRef.current += 1;
    simAbortRef.current?.abort();
    signersSeqRef.current += 1;
  }, []);

  // Assinatura dos dados que afetam a simulacao: so muda quando recebiveis
  // COMPLETOS mudam (ou taxa/cedente) — nao dispara a cada tecla em recebivel
  // ainda incompleto.
  const simSignature = useMemo(
    () => JSON.stringify({
      r: validReceivables.map(mapReceivablePayload),
      taxa: taxaNum,
      ced: cedente?.cnpj || "",
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [validReceivables, taxaNum, cedente]
  );

  // Simulacao AUTOMATICA (debounce 600ms): dispara quando os dados do
  // recebivel ficam completos e a cada alteracao subsequente. Escopada a
  // etapa 3 e com `step` nas deps: trocar de step cancela o timer pendente
  // (cleanup) e a request em voo — na transicao 3→4 so a re-simulacao da
  // revisao roda (uma unica chamada).
  useEffect(() => {
    if (validReceivables.length === 0) {
      setSimulationResult(null);
      setSimulationError(null);
      return;
    }
    if (stepKey !== "recebiveis") return;
    const t = setTimeout(() => runSimulation(), simulationDelayMs(600, rateLimited));
    return () => {
      clearTimeout(t);
      simAbortRef.current?.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [simSignature, stepKey, rateLimited]);

  // Re-simula ao entrar na revisao final: numeros frescos antes do confirmar.
  useEffect(() => {
    if (stepKey === "revisao" && validReceivables.length > 0) runSimulation();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stepKey]);

  // Bloqueio terminal recém-detectado: foco programático no card (leitor de
  // tela anuncia; teclado continua do ponto certo).
  useEffect(() => {
    if (terminalError) terminalRef.current?.focus();
  }, [terminalError]);

  // ── Veredito consultivo da policy (item 16) ──────────────────────────────
  // Deriva SEMPRE do simulationResult corrente — nunca de resposta stale:
  // (a) runSimulation so aceita resposta com seq === simSeqRef.current (a
  //     superada e descartada e a request em voo abortada);
  // (b) qualquer edicao de dado limpa o resultado (clearSimulation) — sem
  //     veredito nao ha bloqueio (fail-safe: V2 antiga sem o bloco idem).
  // Bloqueio = so exibicao/avanco; o enforcement real segue no create (422).
  // useMemo: getPolicyVerdict avisa (console.warn) em desvio de contrato da
  // V2 — memoizar garante no maximo 1 warn por RESPOSTA, nao 1 por render.
  const policyVerdict = useMemo(() => getPolicyVerdict(simulationResult), [simulationResult]);
  const policyRejected = policyVerdict?.would_reject === true; // = isSimulationRejected(simulationResult)
  // Faixa permitida e motivo da V2 citam TAXA — no modo reduzido (01(b)) não
  // vão à tela: copy genérica, sem faixa.
  const allowedRangeText = cfg.showRates ? formatAllowedRange(policyVerdict?.allowed_range) : null;
  const policyRejectText = cfg.showRates
    ? policyRejectionReason(policyVerdict)
    : CEDENTE_POLICY_REJECT_COPY;

  // ── Vocabulário CANÔNICO da V2 na LEITURA da simulação (PUBLIC_ALIASES) ────
  // Fonte do objeto: POST /v2/simulate (proxy passthrough cru do Portal) → V2
  // POST /v1/portal/simulate → `PortalSimulationResponse(SimulationResponse)`,
  // que é `AdditiveVocabularyOut`: na 1.x a resposta traz os DOIS nomes; na 2.0
  // só o canônico. Por isso `canônico ?? deprecado`, seleção por NULLISH e
  // NUNCA por `||` — um `0` canônico legítimo não pode cair no nome deprecado.
  // Nada de ENVIO muda aqui: o body que o wizard monta segue no vocabulário do
  // Portal (contrato interno front↔portal; o backend do Portal traduz na saída).
  const oprGrossValue =
    simulationResult?.opr_gross_future_value ?? simulationResult?.opr_gross_face_value;
  const oprLiquidValue =
    simulationResult?.opr_net_present_liquid_value ?? simulationResult?.opr_liquid_value;
  const oprDiscountValue =
    simulationResult?.opr_present_value_discount ?? simulationResult?.opr_discounted_value;
  // Money-path: há valor líquido REAL para mostrar (e para confirmar em cima)?
  // A V2 manda strings — `!!"0.00"` é `true`, e era exatamente o "R$ 0,00
  // fabricado" que aparecia quando o valor não vinha.
  const liquidoVisivel = hasVisibleLiquidValue(oprLiquidValue);

  const submit = async () => {
    if (!product || !cedente || validReceivables.length === 0) {
      // Round 5 / B6 — este early-return existia MUDO: clicar em "Confirmar e
      // criar operação" não fazia nada, sem POST, sem erro, sem explicação.
      // Alcançável ainda hoje sem rascunho nenhum (deep-link `?cedente_id=` com
      // id inválido passa o gate da etapa, que só olha se o id está preenchido).
      // A correção do passo restaurado fecha a porta do rascunho; esta aqui tira
      // o silêncio de TODAS as portas — nada do que é enviado muda.
      const faltando = [
        !product && "o produto",
        !cedente && "o cedente",
        validReceivables.length === 0 && "ao menos um recebível completo",
      ].filter(Boolean);
      toast.error(
        `${faltando.length > 1 ? "Faltam" : "Falta"} ${faltando.join(" e ")} para criar a ` +
          "operação. Volte às etapas anteriores e complete — se a tela não carregou, tente de novo."
      );
      return;
    }
    // Defesa em profundidade: o gate do botão já barra (simulationError), mas
    // enviar `taxa_mensal: NaN` (→ null no JSON) faria a V2 aplicar a policy
    // como se o campo estivesse vazio.
    if (taxaErro) {
      toast.error(taxaErro);
      return;
    }
    // Defesa em profundidade (o avanço da etapa já bloqueia): PJ sem seleção
    // válida de assinantes volta à etapa em vez de colher o 422 da V2.
    if (cedenteIsPJ && !canNextAssinantes) {
      setStep(stepFlow.indexOf("assinantes") + 1);
      return;
    }
    setSubmitting(true);
    try {
      const body = {
        product_id: product.id,
        // Derivados dos recebiveis (a etapa de valores manuais foi removida):
        valor: totalReceivables,
        descricao,
        receivables: validReceivables.map(mapReceivablePayload),
      };
      if (!isCedenteMode) {
        // No modo cedente o servidor recusa (422 `extra_forbid`) qualquer um
        // destes: o cedente vem do token, o prazo é derivado lá e taxa NUNCA
        // trafega neste ramo.
        body.cedente_id = cedente.id;
        body.prazo_dias = prazoDias;
        body.taxa_mensal = taxaNum;
      }
      // Assinantes do contrato (item 22): só cedente PJ — a V2 exige 1..7 via
      // Portal; PF assina ele mesmo (enviar signer_ids p/ PF é 422 na V2).
      if (cedenteIsPJ) {
        body.signer_ids = selectedSignerIds;
      }
      const { data } = await api.post(cfg.endpoints.create, body);
      // FS-161: a operação EXISTE no servidor — o rascunho morre AQUI, antes do
      // navigate. Depois seria tarde: a tela some, e um rascunho sobrevivente
      // ofereceria "restaurar" uma operação que já foi criada (risco de
      // duplicata). `operationCreated` desarma o aviso de saída e cala o
      // autosave pendente, para que o debounce não regrave o que se apagou.
      clearDraft(mode, userId);
      setOperationCreated(true);
      toast.success(cfg.copy.sucesso(data.numero));
      navigate(backPath);
    } catch (e) {
      // Fail-closed no backend: se a V2 recusou, NADA foi criado. O detail
      // estruturado (ex. CET_OUT_OF_BOUNDS) vira mensagem clara com a faixa
      // (parceiro) ou copy genérica sem taxa (cedente).
      const rawDetail = e?.response?.data?.detail;
      const info = describeApiError(e, "Falha ao criar operacao");
      const msg = info.message;
      toast.error(msg);
      if (info.terminal) {
        // Terminal só no create (a simulação passou e a config mudou no meio):
        // volta ao passo Recebíveis, onde vive o card de bloqueio.
        setTerminalError({ message: msg, scope: info.scope });
        setStep(stepFlow.indexOf("recebiveis") + 1);
        return;
      }
      // 422 de assinantes (signers_required / invalid_signer / ...): mensagem
      // da V2 já é acionável — volta à etapa Assinantes com a lista FRESCA
      // (um invalid_signer típico é representante arquivado nesse meio-tempo).
      // O teste é sobre o detail CRU: no modo cedente a copy exibida é nossa.
      const signerProbe = typeof rawDetail === "string" ? rawDetail : (typeof msg === "string" ? msg : "");
      if (cedenteIsPJ && SIGNER_ERROR_RE.test(signerProbe)) {
        setStep(stepFlow.indexOf("assinantes") + 1);
        loadSigners();
      }
    } finally {
      setSubmitting(false);
    }
  };

  const canNext1 = !!productId && product?.enabled;
  const canNext2 = !!cedenteId;
  // Linha parcial bloqueia: o payload envia so os validos e uma linha
  // preenchida pela metade seria descartada em silencio. Veredito REJEITADO
  // da simulacao corrente tambem bloqueia (item 16): sem isso o usuario
  // avancava 2 etapas para so entao descobrir a taxa fora da faixa no 422.
  const canNext3 = validReceivables.length > 0 && !hasPartialReceivable && !policyRejected;
  // Assinantes (PJ): 1..7 selecionados, todos COMPLETOS p/ assinar (telefone +
  // endereço — espelho do gate signer_incomplete_for_contract da V2) e lista
  // carregada sem erro (fail-closed: sem lista confiável não se avança).
  const canNextAssinantes =
    !signersError &&
    !signersLoading &&
    selectedSigners.length >= 1 &&
    selectedSigners.length <= MAX_SIGNERS &&
    incompleteSelected.length === 0;

  // Sacado do modo cedente: sem cliente cadastrado não há operação possível.
  const semClientes = isCedenteMode && !productsLoading && !loadError && clientes.length === 0;

  // ═══ FS-161 — anti-perda (guard + rascunho) ════════════════════════════════
  // (a) GUARD: `beforeunload` cobre F5/aba/URL; os botões do PRÓPRIO wizard
  //     pedem confirmação no AlertDialog abaixo. Menu lateral e logout ficam
  //     FORA por decisão do owner (P6) — cobri-los exigiria migrar o router
  //     (BrowserRouter declarativo não expõe `useBlocker`), e o rascunho de (b)
  //     já evita a perda nesses caminhos.
  // (b) RASCUNHO: sessionStorage, TTL 24h, por usuário e por modo — a regra
  //     inteira vive em lib/operationDraft.js.

  // Só o que o USUÁRIO digitou entra no rascunho (nada de resultado de
  // simulação, erro ou lista de servidor) — o objeto é o contrato da lib.
  const draftState = useMemo(
    () => ({ productId, cedenteId, taxa, descricao, receivables, selectedSignerIds, step }),
    [productId, cedenteId, taxa, descricao, receivables, selectedSignerIds, step]
  );
  const draftSig = useMemo(() => draftSignature(draftState), [draftState]);

  // BASELINE do "mexeu?": tirada no primeiro render em que o load inicial
  // terminou — aí o preenchimento programático (deep-link `?cliente_id=`, que
  // preenche sacado E documento; cedente vindo do token) JÁ está no estado.
  // Sem isso a tela nasceria "suja" com deep-link: aviso de perda que o usuário
  // não causou e rascunho-fantasma gravado sem ninguém digitar.
  useEffect(() => {
    if (draftBaselineRef.current !== null || productsLoading) return;
    draftBaselineRef.current = draftSig;
  }, [productsLoading, draftSig]);

  // Sujo = DIVERGE da baseline. Derivado (nada de flag manual que dessincroniza)
  // e simétrico: desfazer as edições volta a limpo.
  const isDirty = !operationCreated && isDirtySince(draftBaselineRef.current, draftState);
  useUnsavedChangesWarning(isDirty);

  // Espelho do estado corrente para o FLUSH: o handler de saída grava o que
  // existe AGORA, sem depender do fechamento de um render antigo.
  useEffect(() => {
    draftStateRef.current = draftState;
  }, [draftState]);

  const gravarRascunho = useCallback(() => {
    if (!userId) return;
    // O retorno importa: storage cheio/bloqueado significa que NÃO há rascunho,
    // e a tela não pode prometer o que não tem.
    setDraftSaveFailed(!saveDraft(mode, userId, draftStateRef.current));
  }, [mode, userId]);

  // Autosave com debounce: o timer reinicia a cada tecla e só grava quando o
  // usuário para de digitar.
  useEffect(() => {
    if (!userId || operationCreated || draftFound || !isDirty) return undefined;
    const t = setTimeout(() => {
      draftTimerRef.current = null;
      gravarRascunho();
    }, DRAFT_DEBOUNCE_MS);
    draftTimerRef.current = t;
    return () => {
      clearTimeout(t);
      draftTimerRef.current = null;
    };
  }, [userId, operationCreated, draftFound, isDirty, draftSig, gravarRascunho]);

  // FLUSH: grava agora o que o debounce ainda deve. Sem isto, sair da tela
  // logo após digitar perderia os últimos ≤800ms — justamente o campo que o
  // usuário acabou de preencher antes de desistir.
  const flushDraft = () => {
    if (draftTimerRef.current === null) return;
    clearTimeout(draftTimerRef.current);
    draftTimerRef.current = null;
    gravarRascunho();
  };

  // Há (ou haverá) rascunho de verdade para prometer ao usuário? Sem usuário
  // identificado nada é persistido; com storage recusando, idem.
  const rascunhoDisponivel = !!userId && !draftSaveFailed;

  // Round 4 / C5 — aviso DERIVADO do que ainda falta, não uma frase guardada:
  // some no instante em que o usuário escolhe o item que faltava, e por
  // construção não consegue acompanhá-lo até a Revisão (os gates `canNext1`/
  // `canNext2` não deixam passar da etapa com o campo vazio). Um deep-link
  // `?cedente_id=` válido também não gera aviso nenhum: o campo não está vazio,
  // logo não há o que o usuário precise resolver.
  const draftNotice = useMemo(
    () =>
      draftUnavailableNotice({
        produto: !!draftDropped?.produto && !productId,
        cedente: !!draftDropped?.cedente && !cedenteId,
      }),
    [draftDropped, productId, cedenteId]
  );

  // Detecção do rascunho: UMA vez, e só depois do load inicial — antes disso as
  // respostas em voo (cedente do token, deep-link de cliente) ainda podem
  // sobrescrever o que acabou de ser restaurado.
  useEffect(() => {
    if (draftCheckedRef.current || !userId || productsLoading) return;
    draftCheckedRef.current = true;
    const found = loadDraft(mode, userId);
    if (found) setDraftFound(found);
  }, [userId, productsLoading, mode]);

  // a11y: o banner de rascunho é anunciado e recebe o foco ao aparecer — molde
  // do card de bloqueio terminal (`terminalRef`) desta mesma tela.
  useEffect(() => {
    if (draftFound) draftBannerRef.current?.focus();
  }, [draftFound]);

  // Round 2 / U1 — MEXER no formulário com o banner na tela é escolha implícita
  // de NÃO restaurar: o banner sai e o autosave arma (o rascunho velho passa a
  // ser sobrescrito pelo novo). Antes disto, quem ignorava o banner e digitava
  // ficava sem autosave nenhum — e o diálogo de saída prometia um rascunho de
  // 24h que não existia. Como é por divergência de assinatura, cobre TODO campo
  // do formulário, sem depender de lembrar de chamar algo em cada handler.
  useEffect(() => {
    if (!draftFound || !isDirty) return;
    setDraftFound(null);
  }, [draftFound, isDirty]);

  const restoreDraft = () => {
    const s = draftFound?.state;
    setDraftFound(null);
    if (!s) return;
    // Round 4 / B5 — revalidar SÓ contra lista que carregou. Um 500 no
    // `/products` ou no `/cedentes` deixa a lista vazia, e vazia a revalidação
    // acusaria "sumiu" para um id PERFEITAMENTE VÁLIDO: a escolha certa sairia
    // do estado e o autosave regravaria o rascunho sem ela — um blip de rede
    // apagando trabalho, exatamente o que esta feature existe para impedir.
    // Sem lista confiável, o id volta como estava — e o PASSO desce junto (ver
    // o cálculo do passo, no fim desta função): manter o id sem descer o passo
    // plantava o usuário na Revisão com `product`/`cedente` undefined.
    const produtosOk = !loadFailedParts.includes("products");
    const cedentesOk = !loadFailedParts.includes("me");

    // Round 2 / U6: o rascunho pode ter dormido 24h — o produto pode ter sido
    // desabilitado nesse meio-tempo. Restaurá-lo mudo travaria os gates sem
    // explicar; aqui ele não é restaurado e a tela DIZ o que houve.
    const produto = produtosOk
      ? resolveDraftProduct(products, s.productId)
      : { productId: s.productId || "", unavailable: false };
    setProductId(produto.productId);
    // Round 3 / B1: o CEDENTE também some (arquivado, fora do `?status=active`)
    // — e some pior: sem revalidar, o wizard inteiro é atravessado e o
    // "Confirmar e criar operação" vira no-op MUDO (o `submit` abre com
    // `if (!product || !cedente ...) return;`). No modo cedente não há o que
    // revalidar: o cedente É o usuário do token, e o rascunho não manda nisso.
    const cedenteDoRascunho =
      isCedenteMode || !cedentesOk
        ? { cedenteId: s.cedenteId || cedenteId, unavailable: false }
        : resolveDraftCedente(cedentes, s.cedenteId);
    if (!isCedenteMode && s.cedenteId && !cedenteDoRascunho.unavailable) {
      setCedenteId(cedenteDoRascunho.cedenteId);
    }
    // O que foi DESCARTADO por não existir mais. Vira aviso enquanto o campo
    // correspondente seguir vazio (round 4 / C5) — some sozinho quando o
    // usuário escolhe, e nunca acompanha o usuário até a Revisão.
    setDraftDropped(
      produto.unavailable || cedenteDoRascunho.unavailable
        ? { produto: produto.unavailable, cedente: cedenteDoRascunho.unavailable }
        : null
    );
    setTaxa(s.taxa || "");
    setDescricao(s.descricao || "");
    if (s.receivables?.length) {
      // `_key` não é persistido (é identidade de render): cada linha volta com
      // uma chave NOVA, sobre o molde do recebível vazio — campo que o rascunho
      // não trouxer nasce no default, nunca `undefined`.
      //
      // MERGE FS-160 × FS-161 — CARGA PROGRAMÁTICA. Restaurar rascunho é a
      // ÚNICA carga de dinheiro desta tela, e carga NÃO é digitação: tem de
      // passar pelo regime ESTRITO (`loadMoneyCentsBR`, §Carga da lib), nunca
      // por `maskMoneyCentsBR(v, prev)`. Sobre o que a própria máscara gravou
      // (a string já mascarada, com o NBSP canônico) isto é NO-OP idempotente;
      // o que ele impede é o dia em que alguém trocar por `maskMoneyCentsBR`
      // com `prev` — aí `"100.5"` de um storage editado à mão viraria R$ 10,05
      // e um `"10000"` viraria R$ 100,00 (×100 SILENCIOSO em dado gravado).
      const carregarDinheiro = (r) =>
        CAMPOS_DE_VALOR.reduce(
          (acc, campo) => ({ ...acc, [campo]: loadMoneyCentsBR(acc[campo]) }),
          r
        );
      // FS-162 — FAIL-SAFE do "default 100%" (Q1). O marcador `_advanceTouched`
      // é persistido, mas um rascunho gravado ANTES desta leva não o tem: sem
      // este ramo a linha voltaria como "não tocada" e a primeira correção no
      // valor de face SOBRESCREVERIA o valor a antecipar restaurado — um número
      // de dinheiro que a pessoa digitou, trocado sozinho. Ausente + valor
      // preenchido ⇒ trata como TOCADA. `_pctInput` não é restaurado de
      // propósito: o percentual volta DERIVADO do valor.
      //
      // O teste é sobre o `r` do RASCUNHO, não sobre a linha já mesclada: o
      // molde `emptyReceivable()` traz `_advanceTouched: false`, que apagaria a
      // distinção entre "gravado como não tocada" e "formato anterior".
      const advanceTouchedRestaurado = (r) =>
        typeof r._advanceTouched === "boolean"
          ? r._advanceTouched
          : !!String(r.requested_advance_value ?? "").trim();
      setReceivables(
        s.receivables.map((r) =>
          carregarDinheiro({
            ...emptyReceivable(),
            ...r,
            _advanceTouched: advanceTouchedRestaurado(r),
          })
        )
      );
    }
    if (s.selectedSignerIds?.length) setSelectedSignerIds(s.selectedSignerIds);
    // Volta à ETAPA do problema — a mais cedo que houver. Sem produto não há
    // como estar depois da etapa Produto; sem cedente, depois da etapa Cedente.
    //
    // Round 5 / B6 — LISTA QUE CAIU desce o passo para 1, mesmo mantendo o id.
    // O passo do rascunho é o único ponto do wizard que não passa pelos gates:
    // restaurar direto na Revisão com `products`/`cedentes` vazios deixava
    // `product`/`cedente` undefined, a simulação rodava, o "Confirmar" ficava
    // habilitado — e o clique não fazia nada (o `submit` abre com
    // `if (!product || !cedente ...) return;`). Vai para a etapa Produto, e não
    // para a Cedente, de propósito: é lá que vivem o aviso de falha de carga e
    // o botão de tentar de novo — a única ação que resolve de fato.
    const passoCedente = stepFlow.indexOf("cedente") + 1;
    const algumaListaCaiu = !produtosOk || !cedentesOk;
    if (algumaListaCaiu || produto.unavailable) setStep(1);
    else if (cedenteDoRascunho.unavailable && passoCedente > 0) setStep(passoCedente);
    else if (s.step) setStep(s.step);
    // Nada de número de dinheiro velho na tela: a simulação re-roda a partir do
    // estado restaurado (é dado de servidor, nunca vem do rascunho).
    clearSimulation();
  };

  const discardDraft = () => {
    clearDraft(mode, userId);
    setConfirmDiscard(false);
    setDraftFound(null);
  };

  // Rascunho restaurado pode apontar para um passo que o fluxo ATUAL não tem
  // (PJ tem a etapa Assinantes, PF não). Fora da faixa, nenhuma etapa renderiza
  // — melhor recuar para a última válida do que mostrar tela em branco.
  useEffect(() => {
    if (step > totalSteps) setStep(totalSteps);
  }, [step, totalSteps]);

  // Saída por botão do PRÓPRIO wizard: com formulário sujo, confirma antes.
  // O rascunho SOBREVIVE à saída (é para isso que ele existe) — o descarte é
  // sempre explícito, no banner de restauração.
  //
  // O FLUSH vem ANTES de qualquer coisa: grava o que o debounce ainda devia
  // (≤800ms de digitação) e é ele que atualiza `draftSaveFailed` — o texto do
  // diálogo só pode prometer rascunho depois de tentar gravá-lo de fato.
  const requestExit = (path) => {
    flushDraft();
    if (isDirty) setExitTarget(path);
    else navigate(path);
  };
  const confirmExit = () => {
    flushDraft();
    const destino = exitTarget;
    setExitTarget(null);
    if (destino) navigate(destino);
  };

  // R5 (refatoração mecânica): o que os subcomponentes do wizard leem deste
  // escopo — cada um desestrutura só o que usa (components/newoperation/).
  const ctx = {
    cfg,
    isCedenteMode,
    backPath,
    setStep,
    step,
    products,
    productsLoading,
    boUnavailable,
    catalogState,
    loadFailedParts,
    loadError,
    catalogReason,
    cedentes,
    clientes,
    deepLinkUnresolved,
    clienteDialog,
    setClienteDialog,
    submitting,
    simulating,
    simulationResult,
    simulationError,
    terminalError,
    terminalRef,
    draftFound,
    draftBannerRef,
    confirmDiscard,
    setConfirmDiscard,
    exitTarget,
    setExitTarget,
    productId,
    cedenteId,
    taxa,
    descricao,
    setDescricao,
    receivables,
    loadInitial,
    product,
    enabledCount,
    cedente,
    taxaNum,
    cedenteIsPJ,
    stepFlow,
    stepKey,
    totalSteps,
    signers,
    signersLoading,
    signersError,
    selectedSignerIds,
    repDialog,
    setRepDialog,
    repBasePath,
    handleTaxaChange,
    handleProductChange,
    handleCedenteChange,
    loadSigners,
    selectedSigners,
    incompleteSelected,
    toggleSigner,
    addReceivable,
    removeReceivable,
    updateReceivable,
    updateNetFaceValue,
    updateAdvanceValue,
    updateAdvancePercent,
    blurAdvancePercent,
    finalizeMoneyField,
    percentualIndisponivel,
    percentualTexto,
    percentualAproximado,
    selectPayerCliente,
    handleClienteCreated,
    openClienteDialog,
    faltando,
    rotulosFaltando,
    rotulosVazios,
    rotulosInvalidos,
    payerInvalido,
    validReceivables,
    hasPartialReceivable,
    avisoRecebivelParcial,
    totalReceivables,
    prazoDias,
    runSimulation,
    policyRejected,
    allowedRangeText,
    policyRejectText,
    oprGrossValue,
    oprLiquidValue,
    oprDiscountValue,
    liquidoVisivel,
    submit,
    canNext1,
    canNext2,
    canNext3,
    canNextAssinantes,
    semClientes,
    rascunhoDisponivel,
    draftNotice,
    restoreDraft,
    discardDraft,
    requestExit,
    confirmExit,
  };

  return (
    <div className="max-w-4xl mx-auto space-y-8" data-testid="new-operation-page">
      <WizardHeader ctx={ctx} />

      <RascunhoAvisos ctx={ctx} />

      {/* Stepper — o fluxo é dinâmico: PJ tem a etapa Assinantes; PF não. */}
      <WizardStepper ctx={ctx} />

      {/* Step 1: Produto */}
      {stepKey === "produto" && <StepProduto ctx={ctx} />}

      {/* Step 2: Cedente & Valores (só no modo parceiro) */}
      {stepKey === "cedente" && <StepCedente ctx={ctx} />}

      {/* Step 3: Recebiveis */}
      {stepKey === "recebiveis" && <StepRecebiveis ctx={ctx} />}

      {/* Step Assinantes (só cedente PJ, item 22): quem assina o contrato são
          os representantes do cedente — padrões pré-selecionados, ajuste por
          operação, 1..7, ordem = ordem de assinatura. */}
      {stepKey === "assinantes" && <StepAssinantes ctx={ctx} />}

      {/* Step final: Revisao */}
      {stepKey === "revisao" && <StepRevisao ctx={ctx} />}

      {/* Actions */}
      <WizardActions ctx={ctx} />
    </div>
  );
}
